import React, { useCallback, useMemo, useState } from "react";
import { AgGridTable, LoadingState } from "components";
import { useReport } from "hooks";
import { S7Query } from "types";
import { formatDateSafe } from "utils/helpers/accounting";

const today = new Date();
const formatDate = (d: Date) => d.toISOString().slice(0, 10);
const startOfYear = `${today.getFullYear()}-01-01`;
const todayString = formatDate(today);

const defaultFilters: S7Query = {
  from_date: startOfYear,
  to_date: todayString,
};

const S7Report: React.FC = () => {
  const { fetchS7Hkd } = useReport();
  const [filters, setFilters] = useState<S7Query>(defaultFilters);
  const [submitted, setSubmitted] = useState<S7Query | null>(null);

  const handleFilterChange = useCallback((key: keyof S7Query, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSubmit = useCallback(
    (event?: React.FormEvent) => {
      event?.preventDefault();
      if (!filters.from_date || !filters.to_date) {
        return;
      }
      setSubmitted(filters);
      fetchS7Hkd.mutate(filters);
    },
    [fetchS7Hkd, filters],
  );

  const responseData = fetchS7Hkd.data?.data;
  const summary = responseData?.summary || {
    opening_balance: 0,
    total_cash_in: 0,
    total_cash_out: 0,
    closing_balance: 0,
    is_balanced: true,
  };
  const transactions = responseData?.transactions || [];

  const columns = useMemo(
    () => [
      {
        headerName: "Ngày hạch toán",
        field: "accounting_date",
        minWidth: 140,
        valueFormatter: (params: any) => formatDateSafe(params.value),
      },
      {
        headerName: "Chứng từ",
        field: "voucher_code",
        minWidth: 140,
      },
      {
        headerName: "Diễn giải",
        field: "description",
        minWidth: 260,
        flex: 1,
      },
      {
        headerName: "Thu",
        field: "cash_in",
        minWidth: 140,
        valueFormatter: (params: any) =>
          params.value !== undefined &&
          params.value !== null &&
          params.value > 0
            ? Number(params.value).toLocaleString("vi-VN") + " ₫"
            : "",
        cellClass: "text-right font-semibold text-green-700",
      },
      {
        headerName: "Chi",
        field: "cash_out",
        minWidth: 140,
        valueFormatter: (params: any) =>
          params.value !== undefined &&
          params.value !== null &&
          params.value > 0
            ? Number(params.value).toLocaleString("vi-VN") + " ₫"
            : "",
        cellClass: "text-right font-semibold text-red-700",
      },
      {
        headerName: "Số dư",
        field: "balance",
        minWidth: 140,
        valueFormatter: (params: any) =>
          params.value !== undefined && params.value !== null
            ? Number(params.value).toLocaleString("vi-VN") + " ₫"
            : "",
        cellClass: "text-right font-bold text-blue-700",
      },
      {
        headerName: "Loại tiền",
        field: "cash_type",
        minWidth: 120,
        valueFormatter: (params: any) =>
          params.value === "CASH"
            ? "Tiền mặt"
            : params.value === "BANK"
              ? "Ngân hàng"
              : "",
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      minWidth: 100,
    }),
    [],
  );

  const isLoading = fetchS7Hkd.isPending;

  return (
    <div className="p-6 space-y-6">
      {isLoading && (
        <LoadingState
          message="Đang tải báo cáo"
          description="Vui lòng chờ trong giây lát."
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 uppercase">Báo cáo S7-HKD</p>
          <h1 className="text-2xl font-semibold text-gray-900">Sổ tiền</h1>
          <p className="text-sm text-gray-600">
            Theo dõi dòng tiền thu - chi theo thời gian.
          </p>
        </div>
        <button
          type="button"
          onClick={handleSubmit}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700"
          disabled={isLoading}
        >
          {isLoading ? "Đang tải..." : "Xem báo cáo"}
        </button>
      </div>

      <form
        onSubmit={handleSubmit}
        className="rounded-lg border border-gray-200 bg-white shadow-sm p-4 space-y-4"
      >
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Từ ngày * (ngày hạch toán)
            </label>
            <input
              type="date"
              value={filters.from_date}
              onChange={(e) => handleFilterChange("from_date", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Đến ngày * (ngày hạch toán)
            </label>
            <input
              type="date"
              value={filters.to_date}
              onChange={(e) => handleFilterChange("to_date", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Loại tiền (tùy chọn)
            </label>
            <select
              value={filters.cash_type || ""}
              onChange={(e) =>
                handleFilterChange("cash_type", e.target.value || undefined)
              }
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            >
              <option value="">Tất cả</option>
              <option value="CASH">Tiền mặt</option>
              <option value="BANK">Ngân hàng</option>
            </select>
          </div>
        </div>
        <div className="flex items-center justify-end">
          <button
            type="submit"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            disabled={isLoading}
          >
            Áp dụng bộ lọc
          </button>
        </div>
      </form>

      {submitted && (
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm p-4 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-900 mb-3">
              📊 Tổng hợp dòng tiền
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-gray-600">Số dư đầu kỳ</p>
                <p className="text-xl font-bold text-gray-900">
                  {summary.opening_balance.toLocaleString("vi-VN")} ₫
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Tổng thu</p>
                <p className="text-xl font-bold text-green-700">
                  {summary.total_cash_in.toLocaleString("vi-VN")} ₫
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Tổng chi</p>
                <p className="text-xl font-bold text-red-700">
                  {summary.total_cash_out.toLocaleString("vi-VN")} ₫
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Số dư cuối kỳ</p>
                <p className="text-xl font-bold text-blue-700">
                  {summary.closing_balance.toLocaleString("vi-VN")} ₫
                </p>
                {summary.is_balanced && (
                  <p className="text-xs text-green-600 mt-1">✓ Cân đối</p>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900">
              Kết quả ( {submitted.from_date} → {submitted.to_date} )
            </h2>
            <p className="text-sm text-gray-500">
              {transactions.length} giao dịch
            </p>
          </div>

          <AgGridTable
            rows={transactions}
            columns={columns as any}
            defaultColDef={defaultColDef}
            pagination={false}
            autoHeight
          />
        </div>
      )}
    </div>
  );
};

export default S7Report;
